import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Play, Square, Repeat, Upload, Download, FileText, Save, Wand2, Sparkles,
  Volume2, History as HistoryIcon, ArrowLeft, Lock, Grid3x3, SlidersHorizontal, Globe,
  Mic2, Layers, Radio,
} from "lucide-react";
import {
  analyzeBuffer, generateDemoTracks, buildGraph, renderMixBuffer, QUALITY,
  conformTracks, autoMixTracks, detectVocalAndSections, isVocalCategory,
} from "../lib/audioEngine";
import { encodeToFormat } from "../lib/exporters";
import { processTrackFx, parseKeyRoot } from "../lib/vocalFx";
import { generateHarmonies, leadVocalChain, vocoder, bakeMixChain } from "../lib/vocalPro";
import { masterWithProEngine, generateChoir, addInstruments, generateBandAI } from "../lib/proEngine";
import { api, formatApiError, trackEvent } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { LANGS } from "../lib/translations";
import { AiChatWidget, Markdown } from "../components/AiChat";
import { AiMusicGenerator } from "../components/AiMusicGenerator";
import { MixerConsole } from "../components/MixerConsole";
import { StemsPanel } from "../components/StemsPanel";
import { TxSelect } from "../components/TxSelect";
import { Logo } from "../components/Logo";

const CAT_COLORS = {
  drum: "#EF4444", bass: "#F59E0B", guitar: "#10B981", brass: "#06B6D4",
  strings: "#3B82F6", synth: "#8B5CF6", lead: "#EC4899",
  voice_lead: "#FF5500", voice_choir: "#F97316", other: "#64748B",
};

let TID = 0;
const uid = () => `t${++TID}`;

export default function Studio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang, setLang } = useLang();
  const isSub = !!user && (user.role === "admin" || ["Standard", "High / Studio", "Master Pro"].includes(user.plan));
  const ctxRef = useRef(null);
  const playRef = useRef(null);
  const rafRef = useRef(null);
  const wakeRef = useRef(null);
  const canvasRef = useRef(null);

  const [tracks, setTracks] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiPro, setAiPro] = useState("");
  const [aiProLoading, setAiProLoading] = useState(false);
  const [mixName, setMixName] = useState("Mon Mix TUBMIX");
  const [format, setFormat] = useState("WAV");
  const [proMaster, setProMaster] = useState(null); // {url, report}
  const [proBusy, setProBusy] = useState(false);
  const [choirBusy, setChoirBusy] = useState(false);
  const [arrangeBusy, setArrangeBusy] = useState(false);
  const [arrangeStyle, setArrangeStyle] = useState("pop");
  const [arrangeResult, setArrangeResult] = useState(null); // {mixUrl, blob, report}
  const [bandBusy, setBandBusy] = useState(false);
  const [bandResult, setBandResult] = useState(null); // {url, blob, report}
  const [bandPrompt, setBandPrompt] = useState("");
  const [comboBusy, setComboBusy] = useState(false);

  const [params, setParams] = useState({
    masterGain: 0.9, compThreshold: -18, compRatio: 3,
    reverb: false, reverbAmount: 0.25, delay: false, delayAmount: 0.2,
    sidechain: false, lufsTarget: -14, quality: "Standard", loop: true, mono: false,
  });

  const getCtx = () => {
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return ctxRef.current;
  };

  const loadDemo = useCallback(async () => {
    setAnalyzing(true);
    try {
      const ctx = getCtx();
      const demo = await generateDemoTracks(ctx);
      const built = demo.map((d) => {
        const analysis = analyzeBuffer(d.buffer);
        return {
          id: uid(), name: d.name, category: analysis.category, buffer: d.buffer, analysis, isDemo: true,
          vocal: analysis.vocal, voiceType: analysis.voiceType,
          gain: 0, pan: 0, mute: false, solo: false, hpf: false, hpfFreq: 80,
          eqLow: 0, eqMid: 0, eqHigh: 0, deEsser: false, gate: false, autoTune: false, playBuffer: null,
        };
      });
      setTracks(built);
      toast.success(`${built.length} ${t("studio.demoLoaded")}`);
    } finally { setAnalyzing(false); }
  }, []); // eslint-disable-line

  useEffect(() => { trackEvent("pageview", "studio", "/studio"); loadDemo(); loadHistory(); return () => cleanup(); }, []); // eslint-disable-line

  const loadHistory = async () => {
    if (localStorage.getItem("tubmix_token")) {
      try { const { data } = await api.get("/mixes"); setHistory(data.mixes); return; } catch (e) {}
    }
    const local = JSON.parse(localStorage.getItem("tubmix_history") || "[]");
    setHistory(local);
  };

  const cleanup = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (playRef.current) { try { playRef.current.sources.forEach((s) => s.src.stop()); } catch (e) {} }
    if (wakeRef.current) { try { wakeRef.current.release(); } catch (e) {} }
  };

  const handleFiles = async (files) => {
    const arr = Array.from(files).filter((f) => f.type.startsWith("audio") || /\.(wav|mp3|ogg|m4a|flac|aac)$/i.test(f.name));
    if (!arr.length) return;
    const existing = tracks.filter((t) => !t.isDemo);
    if (existing.length + arr.length > 28) { toast.error(t("studio.maxTracks")); return; }
    const hadDemo = tracks.some((t) => t.isDemo);
    setAnalyzing(true);
    const ctx = getCtx();
    const added = [];
    for (const f of arr) {
      try {
        const buf = await ctx.decodeAudioData(await f.arrayBuffer());
        const analysis = analyzeBuffer(buf);
        added.push({
          id: uid(), name: f.name.replace(/\.[^.]+$/, ""), category: analysis.category, buffer: buf,
          analysis, vocal: analysis.vocal, voiceType: analysis.voiceType,
          gain: 0, pan: 0, mute: false, solo: false, hpf: false, hpfFreq: 80,
          eqLow: 0, eqMid: 0, eqHigh: 0, deEsser: false,
        });
      } catch (e) { toast.error(`${t("studio.decodeFail")} ${f.name}`); }
    }
    setTracks((t) => [...t.filter((x) => !x.isDemo), ...added]);
    setAnalyzing(false);
    if (added.length) {
      setSynced(false); setVoiceInfo(null);
      toast.success(`${added.length} ${t("studio.importedTracks")}${hadDemo ? t("studio.demoRemoved") : ""}`);
    }
  };

  const onDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };

  const updateTrack = (id, patch) => setTracks((ts) => ts.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const removeTrack = (id) => setTracks((ts) => ts.filter((t) => t.id !== id));

  // --- stems / groups ---
  const setStemGain = (fam, ids, val) => {
    const delta = val - (stemTrims[fam] || 0);
    setTracks((ts) => ts.map((x) => (ids.includes(x.id) ? { ...x, gain: Math.max(-24, Math.min(12, x.gain + delta)) } : x)));
    setStemTrims((s) => ({ ...s, [fam]: val }));
  };
  const setStemMute = (ids, mute) => setTracks((ts) => ts.map((x) => (ids.includes(x.id) ? { ...x, mute } : x)));
  const setStemSolo = (ids, solo) => setTracks((ts) => ts.map((x) => (ids.includes(x.id) ? { ...x, solo } : x)));

  // manual role override — the user tells the AI what each track is (never mis-detects)
  const setRole = (id, category) => {
    const vocal = category === "voice_lead" || category === "voice_choir";
    const voiceType = category === "voice_choir" ? "choir" : category === "voice_lead" ? "lead" : null;
    updateTrack(id, { category, vocal, voiceType });
  };

  const toggleFx = async (id, fxKey) => {
    const tr = tracks.find((x) => x.id === id);
    if (!tr) return;
    const flags = { gate: tr.gate, deEsser: tr.deEsser, autoTune: tr.autoTune, [fxKey]: !tr[fxKey] };
    updateTrack(id, { [fxKey]: !tr[fxKey] });
    const anyOn = flags.gate || flags.deEsser || flags.autoTune;
    if (!anyOn) { updateTrack(id, { playBuffer: null }); return; }
    setAnalyzing(true);
    try {
      const ctx = getCtx();
      const pb = await processTrackFx(ctx, tr.buffer, flags, parseKeyRoot(tr.analysis.key));
      updateTrack(id, { playBuffer: pb });
      const names = [flags.gate && "Gate", flags.deEsser && "Dé-esser", flags.autoTune && "Auto-Tune"].filter(Boolean).join(" + ");
      toast.success(`${t("studio.fxApplied")} ${names}`);
    } catch (e) {
      toast.error(t("studio.fxError") + " " + e.message);
    } finally { setAnalyzing(false); }
  };

  // live control while playing
  useEffect(() => {
    if (!playing || !playRef.current) return;
    const anySolo = tracks.some((t) => t.solo);
    playRef.current.sources.forEach((s) => {
      const t = tracks.find((x) => x.id === s.trackId);
      if (!t) return;
      const audible = anySolo ? t.solo : !t.mute;
      s.gain.gain.value = audible ? Math.pow(10, t.gain / 20) : 0;
      s.panner.pan.value = t.pan;
    });
    playRef.current.master.gain.value = params.masterGain;
  }, [tracks, params.masterGain, playing]);

  const play = async () => {
    if (!tracks.length) { toast.error(t("studio.loadFirst")); return; }
    const ctx = getCtx();
    await ctx.resume();
    stop(true);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.connect(ctx.destination);
    const anySolo = tracks.some((t) => t.solo);
    const bpm = tracks.find((t) => t.category === "drum")?.analysis.bpm || tracks[0]?.analysis.bpm || 120;
    const { sources, master } = buildGraph(ctx, tracks, { ...params, anySolo, bpm }, analyser);
    const startAt = ctx.currentTime + 0.05;
    sources.forEach(({ src }) => src.start(startAt));
    playRef.current = { sources, analyser, master, startAt };
    setPlaying(true);
    requestWakeLock();
    drawViz();
    const tick = () => {
      setElapsed(ctx.currentTime - startAt);
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  };

  const stop = (silent) => {
    if (playRef.current) {
      try { playRef.current.sources.forEach((s) => s.src.stop()); } catch (e) {}
      playRef.current = null;
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (!silent) { setPlaying(false); setElapsed(0); }
    if (wakeRef.current) { try { wakeRef.current.release(); wakeRef.current = null; } catch (e) {} }
  };

  const requestWakeLock = async () => {
    try { if ("wakeLock" in navigator) wakeRef.current = await navigator.wakeLock.request("screen"); } catch (e) {}
  };

  const drawViz = () => {
    const canvas = canvasRef.current;
    if (!canvas || !playRef.current) return;
    const analyser = playRef.current.analyser;
    const ctx2d = canvas.getContext("2d");
    const bins = analyser.frequencyBinCount;
    const data = new Uint8Array(bins);
    const loop = () => {
      if (!playRef.current) return;
      analyser.getByteFrequencyData(data);
      const w = canvas.width, h = canvas.height;
      ctx2d.clearRect(0, 0, w, h);
      const bw = w / bins;
      for (let i = 0; i < bins; i++) {
        const v = data[i] / 255;
        const bh = v * h;
        const grad = ctx2d.createLinearGradient(0, h, 0, h - bh);
        grad.addColorStop(0, "#FF5500");
        grad.addColorStop(1, "#6366F1");
        ctx2d.fillStyle = grad;
        ctx2d.fillRect(i * bw, h - bh, bw - 1, bh);
      }
      requestAnimationFrame(loop);
    };
    loop();
  };

  const runAiSuggest = () => {
    if (!tracks.length) return;
    const cats = tracks.map((t) => t.category);
    const bpm = Math.round(tracks.reduce((a, t) => a + t.analysis.bpm, 0) / tracks.length);
    const hasKick = cats.includes("drum");
    const hasBass = cats.includes("bass");
    const hasLead = cats.includes("lead") || cats.includes("choir");
    let style = "Électronique";
    if (bpm < 90) style = "Hip-Hop / Lo-Fi";
    else if (bpm < 115) style = "Pop / R&B";
    else if (bpm < 130) style = "House / Deep";
    else style = "Techno / EDM";
    const tips = [];
    if (hasKick && hasBass) tips.push(t("studio.tipSidechain"));
    if (hasLead) tips.push(t("studio.tipDeEsser"));
    tips.push(`${t("studio.tipLufs")} ${bpm > 125 ? t("studio.lufsClub") : t("studio.lufsStream")}.`);
    tips.push(`${t("studio.tipComp")} ${bpm > 125 ? "4:1" : "3:1"}, ${t("studio.tipCompEnd")}`);
    const text = `${t("studio.styleDetected")} : ${style} · ${bpm} BPM\n\n${tips.map((t) => "• " + t).join("\n")}`;
    setAiText(text);
    // apply a sensible preset
    setParams((p) => ({ ...p, sidechain: hasKick && hasBass, lufsTarget: bpm > 125 ? -9 : -14 }));
    toast.success(t("studio.suggestApplied"));
  };

  const [synced, setSynced] = useState(false);
  const [voiceInfo, setVoiceInfo] = useState(null);
  const [vocMod, setVocMod] = useState("");
  const [vocCarrier, setVocCarrier] = useState("");
  const [stemTrims, setStemTrims] = useState({});

  const runVoiceAnalysis = () => {
    if (!tracks.length) { toast.error(t("studio.loadFirst")); return; }
    const info = detectVocalAndSections(tracks);
    if (!info) { toast.error(t("studio.noUsableTrack")); return; }
    // Apply a dedicated vocal chain to the detected vocal track
    updateTrack(info.vocalId, { hpf: true, hpfFreq: 110, deEsser: true, eqHigh: 2.5, eqMid: 1 });
    setVoiceInfo(info);
    const refrains = info.sections.filter((s) => s.type === "refrain").length;
    trackEvent("voice_analysis", info.vocalName);
    toast.success(`${t("studio.voiceDetected1")} « ${info.vocalName} » · ${refrains} ${t("studio.voiceDetected2")}`);
  };

  const runSync = async () => {
    if (!tracks.length) { toast.error(t("studio.loadFirst")); return; }
    const wasPlaying = playing;
    if (wasPlaying) stop(true);
    const ctx = getCtx();
    const res = conformTracks(ctx, tracks);
    setTracks(res.tracks);
    setParams((p) => ({ ...p, bpm: res.bpm }));
    setSynced(true);
    trackEvent("sync_tracks", `${res.bpm}bpm`);
    toast.success(`${t("studio.syncDone1")} · ${res.bpm} BPM · ${res.bars} ${t("studio.syncDone2")}`);
  };

  const runAutoMix = () => {
    if (!tracks.length) { toast.error(t("studio.loadFirst")); return; }
    const settings = autoMixTracks(tracks);
    const byId = Object.fromEntries(settings.map((s) => [s.id, s.patch]));
    setTracks((ts) => ts.map((t) => (byId[t.id] ? { ...t, ...byId[t.id] } : t)));
    const cats = tracks.map((t) => t.category);
    const hasKick = cats.includes("drum"), hasBass = cats.includes("bass");
    const bpm = Math.round(tracks.reduce((a, t) => a + (t.analysis?.bpm || 120), 0) / tracks.length);
    setParams((p) => ({ ...p, sidechain: hasKick && hasBass, lufsTarget: bpm > 125 ? -9 : -14, compThreshold: -18, compRatio: bpm > 125 ? 4 : 3 }));
    trackEvent("auto_mix", `${tracks.length}tracks`);
    toast.success(`${t("studio.automixDone1")} ${tracks.length} ${t("studio.automixDone2")}`);
  };

  // One-click AI Master Balance: analyze + stabilize each track (freq EQ, gain-staging,
  // ---- Server-side PRO engine (pedalboard + pyloudnorm + librosa) ----
  const runProMaster = async () => {
    if (!tracks.length) { toast.error(t("studio.loadFirst")); return; }
    setProBusy(true);
    stop();
    try {
      const settings = {
        lufs_target: params.lufsTarget ?? -14,
        reverb: !!params.reverb, reverb_amount: params.reverbAmount ?? 0.14,
        delay: !!params.delay, sidechain: !!params.sidechain,
      };
      const { blob, report } = await masterWithProEngine(tracks, settings);
      if (proMaster?.url) URL.revokeObjectURL(proMaster.url);
      const url = URL.createObjectURL(blob);
      setProMaster({ url, report, blob });
      toast.success(`${t("studio.proDone")} ${report.lufs_out ?? "?"} LUFS · ${report.true_peak_db ?? "?"} dBTP`);
    } catch (e) {
      toast.error(t("studio.proError") + " " + formatApiError(e));
    } finally { setProBusy(false); }
  };

  const downloadProMaster = () => {
    if (!proMaster?.blob) return;
    triggerDownload(proMaster.blob, `${mixName} [Master Pro 24-bit].wav`);
  };

  const runProChoir = async () => {
    const lead = tracks.find((x) => x.category === "voice_lead")
      || tracks.find((x) => x.category === "voice_choir")
      || tracks.find((x) => x.vocal);
    if (!lead) { toast.error(t("studio.choirNoVocal")); return; }
    setChoirBusy(true);
    try {
      const { blob, report } = await generateChoir(lead.buffer, {});
      triggerDownload(blob, `${mixName} [Choeur ${report.third || ""}].zip`);
      toast.success(`${t("studio.choirDone")} ${report.key || ""} · ${t("studio.choirThird")} ${report.third || ""}`);
    } catch (e) {
      toast.error(t("studio.choirError") + " " + formatApiError(e));
    } finally { setChoirBusy(false); }
  };

  const pickLeadTrack = () =>
    tracks.find((x) => x.category === "voice_lead")
    || tracks.find((x) => x.category === "voice_choir")
    || tracks.find((x) => x.vocal)
    || tracks.find((x) => x.category === "lead")
    || tracks[0];

  const runArranger = async () => {
    const lead = pickLeadTrack();
    if (!lead) { toast.error(t("studio.loadFirst")); return; }
    setArrangeBusy(true);
    try {
      const { blob, mixUrl, stems, report } = await addInstruments(lead.buffer, { style: arrangeStyle });
      if (arrangeResult?.mixUrl) URL.revokeObjectURL(arrangeResult.mixUrl);
      setArrangeResult({ blob, mixUrl, stems, report });
      toast.success(`${t("studio.arrangeDone")} ${report.key || ""} · ${report.bpm || ""} BPM · ${report.progression || ""}`);
    } catch (e) {
      toast.error(t("studio.arrangeError") + " " + formatApiError(e));
    } finally { setArrangeBusy(false); }
  };

  const runBandAI = async () => {
    const lead = pickLeadTrack();
    if (!lead) { toast.error(t("studio.loadFirst")); return; }
    setBandBusy(true);
    try {
      const { blob, url, report } = await generateBandAI(lead.buffer, arrangeStyle, bandPrompt);
      if (bandResult?.url) URL.revokeObjectURL(bandResult.url);
      setBandResult({ blob, url, report, mastered: false });
      toast.success(t("studio.bandDone"));
    } catch (e) {
      toast.error(t("studio.bandError") + " " + formatApiError(e));
    } finally { setBandBusy(false); }
  };

  // #1 — one-click: generate the neural band THEN master it to -14 LUFS
  const runComboMaster = async () => {
    const lead = pickLeadTrack();
    if (!lead) { toast.error(t("studio.loadFirst")); return; }
    setComboBusy(true);
    try {
      toast.message(t("studio.comboStep1"));
      const band = await generateBandAI(lead.buffer, arrangeStyle, bandPrompt);
      const ctx = getCtx();
      const buf = await ctx.decodeAudioData(await band.blob.arrayBuffer());
      toast.message(t("studio.comboStep2"));
      const { blob, report } = await masterWithProEngine(
        [{ buffer: buf, name: "IA Band", category: "other", gain: 0, pan: null }],
        { lufs_target: params.lufsTarget ?? -14, reverb: true, sidechain: false }
      );
      if (bandResult?.url) URL.revokeObjectURL(bandResult.url);
      const url = URL.createObjectURL(blob);
      setBandResult({ blob, url, report: { ...band.report, ...report }, mastered: true });
      toast.success(`${t("studio.comboDone")} ${report.lufs_out ?? "?"} LUFS`);
    } catch (e) {
      toast.error(t("studio.bandError") + " " + formatApiError(e));
    } finally { setComboBusy(false); }
  };

  // #2 — import generated audio as new mixer tracks
  const importBufferAsTrack = async (arrayBuffer, name, category) => {
    const ctx = getCtx();
    const buf = await ctx.decodeAudioData(arrayBuffer.slice(0));
    const analysis = analyzeBuffer(buf);
    const cat = category || analysis.category;
    return {
      id: uid(), name, category: cat, buffer: buf, analysis,
      vocal: cat === "voice_lead" || cat === "voice_choir", voiceType: analysis.voiceType,
      gain: 0, pan: 0, mute: false, solo: false, hpf: false, hpfFreq: 80,
      eqLow: 0, eqMid: 0, eqHigh: 0, deEsser: false, playBuffer: null,
    };
  };

  const importBandToMixer = async () => {
    if (!bandResult?.blob) return;
    try {
      const tr = await importBufferAsTrack(await bandResult.blob.arrayBuffer(), t("studio.aiBandTrack"), "other");
      setTracks((ts) => [...ts.filter((x) => !x.isDemo), tr]);
      setSynced(false);
      toast.success(t("studio.importedToMixer"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const importArrangementToMixer = async () => {
    const stems = arrangeResult?.stems || {};
    const map = { "guitare.wav": ["Guitare IA", "guitar"], "basse.wav": ["Basse IA", "bass"],
                  "batterie.wav": ["Batterie IA", "drum"], "nappe.wav": ["Nappe IA", "synth"] };
    const toAdd = [];
    for (const [fname, [name, cat]] of Object.entries(map)) {
      if (stems[fname]) {
        try { toAdd.push(await importBufferAsTrack(stems[fname].buffer.slice(stems[fname].byteOffset, stems[fname].byteOffset + stems[fname].byteLength), name, cat)); } catch (e) { /* noop */ }
      }
    }
    if (!toAdd.length) { toast.error(t("studio.importedNone")); return; }
    setTracks((ts) => [...ts.filter((x) => !x.isDemo), ...toAdd]);
    setSynced(false);
    toast.success(`${toAdd.length} ${t("studio.importedToMixer")}`);
  };

  // Deep AI treatment, baked into each track buffer so the user clearly HEARS the
  // compressor), treat lead vocals, and set a balanced master — a finished balanced title.
  const runAiMaster = async () => {
    if (!tracks.length) { toast.error(t("studio.loadFirst")); return; }
    setAnalyzing(true);
    try {
      const ctx = getCtx();
      await new Promise((r) => setTimeout(r, 30));
      const settings = autoMixTracks(tracks);
      const byId = Object.fromEntries(settings.map((s) => [s.id, s.patch]));
      // bake an audible treatment into each track so the result is clearly processed
      const processed = {};
      for (const tk of tracks) {
        await new Promise((r) => setTimeout(r));
        processed[tk.id] = isVocalCategory(tk.category)
          ? leadVocalChain(ctx, tk.buffer, { key: tk.analysis?.key })
          : bakeMixChain(ctx, tk.buffer, tk.category);
      }
      let nVoc = tracks.filter((tk) => isVocalCategory(tk.category)).length;
      setTracks((ts) => ts.map((tk) => {
        const p = byId[tk.id] || {};
        return {
          ...tk,
          pan: p.pan ?? tk.pan,
          gain: p.gain ?? tk.gain,
          hpf: false, eqLow: 0, eqMid: 0, eqHigh: 0,  // baked into the buffer already
          playBuffer: processed[tk.id] || tk.playBuffer,
        };
      }));
      const bpm = Math.round(tracks.reduce((a, t2) => a + (t2.analysis?.bpm || 120), 0) / tracks.length);
      const cats = tracks.map((t2) => t2.category);
      const hasKick = cats.includes("drum"), hasBass = cats.includes("bass");
      setParams((p) => ({ ...p, sidechain: hasKick && hasBass, lufsTarget: bpm > 125 ? -9 : -14, compThreshold: -16, compRatio: bpm > 125 ? 4 : 3, masterGain: 0.9 }));
      trackEvent("ai_master", `${tracks.length}tracks`);
      toast.success(`${t("studio.aiMasterDone1")} ${tracks.length} ${t("studio.aiMasterDone2")}${nVoc ? ` · ${nVoc} ${t("studio.aiMasterDone3")}` : ""}`);
    } catch (e) { toast.error(t("studio.fxError") + " " + e.message); }
    finally { setAnalyzing(false); }
  };

  const vocalTracks = () => tracks.filter((t) => isVocalCategory(t.category) && !t.isHarmony);

  const runHarmonize = async () => {
    const vox = vocalTracks();
    if (!vox.length) { toast.error(t("studio.noVocalTrack")); return; }
    const hasHarm = (v) => tracks.some((x) => x.isHarmony && x.name.startsWith(`${v.name} · `));
    const todo = vox.filter((v) => !hasHarm(v));
    if (!todo.length) { toast.error(t("studio.harmonizeAlready")); return; }
    const room = 28 - tracks.length;
    if (room <= 0) { toast.error(t("studio.maxTracks")); return; }
    setAnalyzing(true);
    try {
      const ctx = getCtx();
      await new Promise((r) => setTimeout(r, 30));
      let added = [];
      for (const v of todo) {
        await new Promise((r) => setTimeout(r));
        const harms = generateHarmonies(ctx, v).map((h) => ({
          id: uid(), name: h.name, category: h.category, buffer: h.buffer,
          analysis: analyzeBuffer(h.buffer), vocal: true, voiceType: "choir", isHarmony: true,
          gain: h.gain, pan: h.pan, mute: false, solo: false, hpf: false, hpfFreq: 80,
          eqLow: 0, eqMid: 0, eqHigh: 0, deEsser: false, gate: false, autoTune: false, playBuffer: null,
        }));
        added = added.concat(harms);
      }
      const capped = added.length > room;
      added = added.slice(0, Math.max(0, room));
      setTracks((ts) => [...ts, ...added]);
      trackEvent("harmonize", `${added.length}voices`);
      toast.success(`${added.length} ${t("studio.harmonizeDone")}${capped ? " " + t("studio.capReached") : ""}`);
    } catch (e) { toast.error(t("studio.fxError") + " " + e.message); }
    finally { setAnalyzing(false); }
  };

  const runLeadVocalPro = async () => {
    const vox = vocalTracks();
    if (!vox.length) { toast.error(t("studio.noVocalTrack")); return; }
    setAnalyzing(true);
    try {
      const ctx = getCtx();
      await new Promise((r) => setTimeout(r, 30));
      const patches = {};
      for (const v of vox) {
        await new Promise((r) => setTimeout(r));
        const pb = leadVocalChain(ctx, v.playBuffer || v.buffer, { key: v.analysis?.key });
        patches[v.id] = pb;
      }
      setTracks((ts) => ts.map((x) => (patches[x.id] ? { ...x, playBuffer: patches[x.id] } : x)));
      trackEvent("lead_vocal_pro", `${vox.length}tracks`);
      toast.success(`${t("studio.leadVocalDone1")} ${vox.length} ${t("studio.leadVocalDone2")}`);
    } catch (e) { toast.error(t("studio.fxError") + " " + e.message); }
    finally { setAnalyzing(false); }
  };

  const runVocoder = async () => {
    const mod = tracks.find((x) => x.id === vocMod);
    const car = tracks.find((x) => x.id === vocCarrier);
    if (!mod || !car) { toast.error(t("studio.vocoderPick")); return; }
    if (tracks.length >= 28) { toast.error(t("studio.maxTracks")); return; }
    setAnalyzing(true);
    try {
      const ctx = getCtx();
      await new Promise((r) => setTimeout(r, 30));
      const out = vocoder(ctx, mod.buffer, car.buffer);
      const analysis = analyzeBuffer(out);
      setTracks((ts) => [...ts, {
        id: uid(), name: `Vocoder · ${mod.name}`, category: "synth", buffer: out, analysis,
        vocal: false, voiceType: null, gain: 0, pan: 0, mute: false, solo: false, hpf: false, hpfFreq: 80,
        eqLow: 0, eqMid: 0, eqHigh: 0, deEsser: false, gate: false, autoTune: false, playBuffer: null,
      }]);
      trackEvent("vocoder", "applied");
      toast.success(t("studio.vocoderDone"));
    } catch (e) { toast.error(t("studio.fxError") + " " + e.message); }
    finally { setAnalyzing(false); }
  };

  const buildAiContext = () => {
    if (!tracks.length) return "Aucune piste chargée pour le moment.";
    const lines = tracks.map((t, i) =>
      `${i + 1}. ${t.name} [${t.category}] — ${t.analysis.bpm} BPM, tonalité ${t.analysis.key}, crest ${t.analysis.crest}dB, peak ${t.analysis.peak}dB, RMS ${t.analysis.rms}dB`);
    return `Projet: ${mixName}\nPistes (${tracks.length}):\n${lines.join("\n")}\n` +
      `Mastering actuel: LUFS cible ${params.lufsTarget}, compresseur seuil ${params.compThreshold}dB ratio ${params.compRatio}:1, ` +
      `reverb ${params.reverb ? "on" : "off"}, delay ${params.delay ? "on" : "off"}, sidechain ${params.sidechain ? "on" : "off"}, ` +
      `qualité ${params.quality}, format d'export ${format}.`;
  };

  const goPricing = () => { navigate("/"); setTimeout(() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" }), 400); };

  const addGeneratedToMixer = (name, buffer) => {
    const existing = tracks.filter((t) => !t.isDemo);
    if (existing.length >= 28) { toast.error(t("studio.maxTracks")); return; }
    trackEvent("add_to_mixer", "ia_track");
    const analysis = analyzeBuffer(buffer);
    setTracks((ts) => [...ts.filter((x) => !x.isDemo), {
      id: uid(), name: (name || "Morceau IA").slice(0, 40), category: analysis.category, buffer, analysis,
      vocal: analysis.vocal, voiceType: analysis.voiceType,
      gain: 0, pan: 0, mute: false, solo: false, hpf: false, hpfFreq: 80,
      eqLow: 0, eqMid: 0, eqHigh: 0, deEsser: false, gate: false, autoTune: false, playBuffer: null,
    }]);
  };

  const runAiPro = async () => {
    if (!tracks.length) { toast.error(t("studio.loadFirst")); return; }
    if (!isSub) { toast.error(t("ai.gatedDesc")); goPricing(); return; }
    setAiProLoading(true);
    try {
      const { data } = await api.post("/ai/mix-suggestions", { summary: buildAiContext(), lang });
      setAiPro(data.suggestions);
      toast.success(t("ai.proTitle"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setAiProLoading(false); }
  };

  const buildReport = () => {
    const lines = [];
    const locale = lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : "en-US";
    lines.push("═══════════════════════════════════════");
    lines.push(`  ${t("studio.reportTitle")}`);
    lines.push(`  ${mixName}`);
    lines.push(`  ${new Date().toLocaleString(locale)}`);
    lines.push("═══════════════════════════════════════\n");
    lines.push(`${t("studio.reportQuality")} : ${params.quality} (${QUALITY[params.quality].label})`);
    lines.push(`Cible LUFS : ${params.lufsTarget} · Compresseur : seuil ${params.compThreshold}dB / ratio ${params.compRatio}:1`);
    lines.push(`Reverb : ${params.reverb ? "ON" : "OFF"} · Delay : ${params.delay ? "ON" : "OFF"} · Sidechain : ${params.sidechain ? "ON" : "OFF"}\n`);
    lines.push(t("studio.reportTracks"));
    tracks.forEach((t2, i) => {
      lines.push(`\n[${i + 1}] ${t2.name}  (${t2.category})`);
      lines.push(`    BPM ${t2.analysis.bpm} · ${t("studio.reportTonality")} ${t2.analysis.key} · Crest ${t2.analysis.crest}dB`);
      lines.push(`    Peak ${t2.analysis.peak}dB · RMS ${t2.analysis.rms}dB`);
      lines.push(`    Gain ${t2.gain}dB · Pan ${t2.pan} · HPF ${t2.hpf ? t2.hpfFreq + "Hz" : "off"} · EQ [${t2.eqLow}/${t2.eqMid}/${t2.eqHigh}]`);
    });
    lines.push(`\n─── ${t("studio.reportGenerated")} ───`);
    return lines.join("\n");
  };

  const downloadReport = () => {
    const blob = new Blob([buildReport()], { type: "text/plain" });
    triggerDownload(blob, `${mixName}.txt`);
    toast.success(t("studio.reportDownloaded"));
  };

  const exportMix = async () => {
    if (!tracks.length) return;
    setExporting(true);
    stop();
    try {
      const anySolo = tracks.some((t) => t.solo);
      const bpm = tracks.find((t) => t.category === "drum")?.analysis.bpm || 120;
      const q = QUALITY[params.quality];
      let sr = q.sampleRate;
      if (format !== "WAV") sr = Math.min(sr, 48000);
      const buffer = await renderMixBuffer(tracks, { ...params, anySolo, bpm }, sr);
      const { blob, ext, fallback } = await encodeToFormat(buffer, format, q.bitDepth);
      triggerDownload(blob, `${mixName} [${params.quality}].${ext}`);
      trackEvent("mix_export", format);
      if (fallback) toast.warning(t("studio.exportFallback"));
      else toast.success(`${t("studio.exportDone")} ${format} ${t("studio.exportDoneEnd")} (${QUALITY[params.quality].label})`);
      await saveMix(ext.toUpperCase());
    } catch (e) {
      toast.error(t("studio.exportError") + " " + e.message);
    } finally { setExporting(false); }
  };

  const saveMix = async (fmt = "WAV") => {
    const format = typeof fmt === "string" ? fmt : "WAV";
    const bpm = Math.round(tracks.reduce((a, t) => a + t.analysis.bpm, 0) / (tracks.length || 1));
    const entry = {
      name: mixName, bpm, key: tracks[0]?.analysis.key || "-", quality: params.quality,
      track_count: tracks.length, lufs: params.lufsTarget, format,
      tracks: tracks.map((t) => ({ name: t.name, category: t.category })),
      report: buildReport(), created_at: new Date().toISOString(),
    };
    if (localStorage.getItem("tubmix_token")) {
      try { await api.post("/mixes", entry); } catch (e) { toast.error(t("studio.cloudSaveFail") + " " + (e.response?.status || e.message)); }
    } else {
      const local = JSON.parse(localStorage.getItem("tubmix_history") || "[]");
      local.unshift(entry);
      localStorage.setItem("tubmix_history", JSON.stringify(local.slice(0, 20)));
    }
    loadHistory();
  };

  const triggerDownload = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const anySolo = tracks.some((t) => t.solo);
  const processedLabel = t("studio.processed");
  const catLabel = (c) => t(`studio.cat.${c}`);
  const voiceBadge = t("studio.voiceBadge");
  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F8FAFC] flex flex-col">
      {/* TRANSPORT HEADER */}
      <header className="sticky top-0 z-30 bg-[#0B0D12]/95 backdrop-blur border-b border-[#262C3A] px-3 sm:px-5 py-3">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate("/")} className="text-[#64748B] hover:text-white transition-colors" data-testid="studio-back-button"><ArrowLeft className="h-5 w-5" /></button>
          <Logo className="h-8 w-8" />
          <input value={mixName} onChange={(e) => setMixName(e.target.value)} data-testid="mix-name-input"
            className="bg-transparent border-b border-transparent hover:border-[#262C3A] focus:border-[#FF5500] outline-none font-heading font-bold text-lg w-44 transition-colors" />

          <div className="flex items-center gap-2 ml-auto">
            <div className="hidden sm:flex items-center gap-0.5 mr-1 rounded-full border border-[#262C3A] p-0.5" data-testid="studio-lang-switcher">
              <Globe className="h-3.5 w-3.5 text-[#64748B] mx-1" />
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => setLang(l.code)} data-testid={`studio-lang-${l.code}`}
                  className={`text-xs font-semibold rounded-full px-2 py-1 transition-colors ${lang === l.code ? "bg-[#FF5500] text-white" : "text-[#94A3B8] hover:bg-[#1A1F2C]"}`}>
                  {l.flag}
                </button>
              ))}
            </div>
            <button onClick={play} disabled={playing} data-testid="play-button"
              className="inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#0ea371] disabled:opacity-40 text-white rounded-xl px-4 py-2 font-semibold transition-colors">
              <Play className="h-4 w-4" /> Play
            </button>
            <button onClick={() => stop()} data-testid="stop-button"
              className="inline-flex items-center gap-2 bg-[#1A1F2C] hover:bg-[#262C3A] rounded-xl px-4 py-2 transition-colors">
              <Square className="h-4 w-4" /> Stop
            </button>
            <button onClick={() => setParams((p) => ({ ...p, loop: !p.loop }))} data-testid="loop-button"
              className={`rounded-xl px-3 py-2 transition-colors ${params.loop ? "bg-[#FF5500] text-white" : "bg-[#1A1F2C] hover:bg-[#262C3A]"}`}>
              <Repeat className="h-4 w-4" />
            </button>
            <span className="font-mono text-sm text-[#64748B] px-2 tabular-nums">{fmt(elapsed)}</span>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row">
        {/* MAIN */}
        <main className="flex-1 p-3 sm:p-5 tx-dark-scroll overflow-auto">
          <AiMusicGenerator getCtx={getCtx} onAddToMixer={addGeneratedToMixer} isSub={isSub} user={user} onUpgrade={goPricing} />
          {/* import + viz */}
          <div className="grid md:grid-cols-3 gap-4 mb-5">
            <div
              onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
              onClick={() => document.getElementById("file-input").click()}
              data-testid="import-dropzone"
              className="md:col-span-1 border-2 border-dashed border-[#262C3A] hover:border-[#FF5500] rounded-2xl p-5 flex flex-col items-center justify-center text-center cursor-pointer transition-colors">
              <Upload className="h-7 w-7 text-[#FF5500] mb-2" />
              <p className="text-sm font-semibold">{t("studio.dropTitle")}</p>
              <p className="text-xs text-[#64748B] mt-1">{t("studio.dropHint")}</p>
              <input id="file-input" type="file" accept="audio/*,.wav,.mp3,.ogg,.m4a,.flac,.aac" multiple hidden
                onChange={(e) => handleFiles(e.target.files)} />
            </div>
            <div className="md:col-span-2 bg-[#131722] border border-[#262C3A] rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-xs uppercase tracking-widest text-[#64748B]">{t("studio.spectrum")} {playing ? "· " + t("studio.live") : ""}</span>
                <button onClick={loadDemo} data-testid="load-demo-button" className="text-xs text-[#FF5500] hover:underline">{t("studio.loadDemo")}</button>
              </div>
              <canvas ref={canvasRef} width={640} height={90} className="w-full h-[90px]" data-testid="spectrum-canvas" />
            </div>
          </div>

          {/* PRO SERVER ENGINE — real studio mix/master + choir */}
          <div className="bg-gradient-to-br from-[#0e2033] to-[#101826] border border-[#1e5a7a] rounded-2xl p-4 mb-4" data-testid="pro-engine">
            <div className="flex items-center gap-2 mb-1">
              <Radio className="h-4 w-4 text-[#06B6D4]" />
              <span className="font-heading font-bold text-white">{t("studio.proEngineTitle")}</span>
              <span className="text-[10px] font-mono uppercase bg-[#06B6D4]/20 text-[#06B6D4] px-2 py-0.5 rounded-full">pedalboard · LUFS</span>
            </div>
            <div className="text-xs text-[#8B98B0] mb-3">{t("studio.proEngineDesc")}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <button onClick={runProMaster} disabled={proBusy} data-testid="pro-master-button"
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#06B6D4] to-[#3B82F6] hover:opacity-90 disabled:opacity-50 text-white rounded-xl py-3 font-bold transition-opacity">
                <Sparkles className="h-5 w-5" /> {proBusy ? t("studio.proMasterBusy") : t("studio.proMasterBtn")}
              </button>
              <button onClick={runProChoir} disabled={choirBusy} data-testid="pro-choir-button"
                className="flex items-center justify-center gap-2 bg-[#0B0D12] hover:bg-[#1c1430] border border-[#262C3A] hover:border-[#EC4899] disabled:opacity-50 text-white rounded-xl py-3 font-bold transition-colors">
                <Layers className="h-5 w-5 text-[#EC4899]" /> {choirBusy ? t("studio.proChoirBusy") : t("studio.proChoirBtn")}
              </button>
            </div>
            <div className="mt-3 grid grid-cols-[1fr_auto] gap-2 items-center">
              <TxSelect
                value={arrangeStyle} onValueChange={setArrangeStyle} testid="arrange-style-select"
                triggerClassName="h-11 px-3" placeholder="Style"
                items={[
                  { value: "pop", label: "Pop" }, { value: "gospel", label: "Gospel" },
                  { value: "lofi", label: "Lo-Fi" }, { value: "afrobeat", label: "Afrobeat" },
                  { value: "rock", label: "Rock" }, { value: "cinematic", label: "Cinématique" },
                ]}
              />
              <span className="text-[11px] text-[#64748B] px-1">{t("studio.styleLabel")}</span>
            </div>
            <button onClick={runArranger} disabled={arrangeBusy} data-testid="pro-arrange-button"
              className="w-full mt-2 flex items-center justify-center gap-2 bg-gradient-to-r from-[#10B981] to-[#06B6D4] hover:opacity-90 disabled:opacity-50 text-white rounded-xl py-3 font-bold transition-opacity">
              <Wand2 className="h-5 w-5" /> {arrangeBusy ? t("studio.arrangeBusy") : t("studio.arrangeBtn")}
            </button>
            <div className="text-[11px] text-[#64748B] mt-1.5">{t("studio.arrangeHint")}</div>
            {arrangeResult && (
              <div className="mt-2 bg-[#0B0D12] border border-[#262C3A] rounded-xl p-3" data-testid="arrange-result">
                <div className="text-xs text-[#94A3B8] mb-2">{t("studio.arrangeListen")} · {arrangeResult.report.key} · {arrangeResult.report.bpm} BPM · {arrangeResult.report.style}</div>
                <audio controls src={arrangeResult.mixUrl} className="w-full h-9 mb-2" data-testid="arrange-audio" />
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => triggerDownload(arrangeResult.blob, `${mixName} [Arrangement ${arrangeResult.report.key || ""}].zip`)}
                    data-testid="arrange-download"
                    className="inline-flex items-center gap-2 bg-[#10B981]/15 hover:bg-[#10B981]/25 text-[#10B981] rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors">
                    <Download className="h-4 w-4" /> {t("studio.arrangeDownload")}
                  </button>
                  <button onClick={importArrangementToMixer} data-testid="arrange-import"
                    className="inline-flex items-center gap-2 bg-[#06B6D4]/15 hover:bg-[#06B6D4]/25 text-[#06B6D4] rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors">
                    <Layers className="h-4 w-4" /> {t("studio.importToMixer")}
                  </button>
                </div>
              </div>
            )}
            <input value={bandPrompt} onChange={(e) => setBandPrompt(e.target.value)}
              data-testid="band-prompt-input" placeholder={t("studio.bandPromptPh")}
              className="w-full mt-3 bg-[#0B0D12] border border-[#262C3A] rounded-lg px-3 py-2 text-sm text-[#F8FAFC] placeholder-[#5b6576] outline-none focus:border-[#A855F7]" />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button onClick={runBandAI} disabled={bandBusy || comboBusy} data-testid="pro-band-button"
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#A855F7] to-[#EC4899] hover:opacity-90 disabled:opacity-50 text-white rounded-xl py-3 font-bold transition-opacity text-sm">
                <Sparkles className="h-5 w-5" /> {bandBusy ? t("studio.bandBusy") : t("studio.bandBtn")}
              </button>
              <button onClick={runComboMaster} disabled={comboBusy || bandBusy} data-testid="pro-combo-button"
                className="flex items-center justify-center gap-2 bg-[#0B0D12] border border-[#A855F7] hover:bg-[#1c1430] disabled:opacity-50 text-white rounded-xl py-3 font-bold transition-colors text-sm">
                <Wand2 className="h-5 w-5 text-[#C084FC]" /> {comboBusy ? t("studio.comboBusy") : t("studio.comboBtn")}
              </button>
            </div>
            <div className="text-[11px] text-[#64748B] mt-1.5">{t("studio.bandHint")}</div>
            {bandResult && (
              <div className="mt-2 bg-[#0B0D12] border border-[#262C3A] rounded-xl p-3" data-testid="band-result">
                <div className="text-xs text-[#94A3B8] mb-2">{t("studio.bandListen")} · {bandResult.report.style}{bandResult.mastered ? ` · Master ${bandResult.report.lufs_out} LUFS` : ""}</div>
                <audio controls src={bandResult.url} className="w-full h-9 mb-2" data-testid="band-audio" />
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => triggerDownload(bandResult.blob, `${mixName} [IA ${bandResult.report.style || ""}].wav`)}
                    data-testid="band-download"
                    className="inline-flex items-center gap-2 bg-[#A855F7]/15 hover:bg-[#A855F7]/25 text-[#C084FC] rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors">
                    <Download className="h-4 w-4" /> {t("studio.bandDownload")}
                  </button>
                  <button onClick={importBandToMixer} data-testid="band-import"
                    className="inline-flex items-center gap-2 bg-[#06B6D4]/15 hover:bg-[#06B6D4]/25 text-[#06B6D4] rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors">
                    <Layers className="h-4 w-4" /> {t("studio.importToMixer")}
                  </button>
                </div>
              </div>
            )}
            {proMaster && (
              <div className="mt-3 bg-[#0B0D12] border border-[#262C3A] rounded-xl p-3" data-testid="pro-master-result">
                <div className="text-xs text-[#94A3B8] mb-2">{t("studio.proListen")}</div>
                <audio controls src={proMaster.url} className="w-full h-9 mb-2" data-testid="pro-master-audio" />
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-[11px] font-mono text-[#8B98B0]" data-testid="pro-master-report">
                    {t("studio.proReportLabel")}: {proMaster.report.lufs_in}→{proMaster.report.lufs_out} LUFS · {proMaster.report.true_peak_db} dBTP · {proMaster.report.bpm} BPM · {proMaster.report.bit_depth}-bit
                  </span>
                  <button onClick={downloadProMaster} data-testid="pro-master-download"
                    className="inline-flex items-center gap-2 bg-[#06B6D4]/15 hover:bg-[#06B6D4]/25 text-[#06B6D4] rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors">
                    <Download className="h-4 w-4" /> {t("studio.proDownloadBtn")}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* PRO AI ACTIONS — real sync + per-track auto-mix */}
          <div className="bg-gradient-to-br from-[#1a1030] to-[#101826] border border-[#3a2a5d] rounded-2xl p-4 mb-4" data-testid="pro-actions">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-heading font-bold text-white">{t("studio.procTitle")}</span>
              {synced && <span className="text-[10px] font-mono uppercase bg-[#10B981]/20 text-[#10B981] px-2 py-0.5 rounded-full" data-testid="synced-badge">{t("studio.synced")} {params.bpm} BPM</span>}
            </div>
            <button onClick={runAiMaster} data-testid="ai-master-button"
              className="w-full flex items-center justify-center gap-2 mb-3 bg-gradient-to-r from-[#FF5500] to-[#8B5CF6] hover:opacity-90 text-white rounded-xl py-3 font-bold transition-opacity">
              <Sparkles className="h-5 w-5" /> {t("studio.aiMasterTitle")}
            </button>
            <div className="grid sm:grid-cols-3 gap-3">
              <button onClick={runSync} data-testid="sync-button"
                className="group flex items-start gap-3 text-left bg-[#0B0D12] hover:bg-[#141b2e] border border-[#262C3A] hover:border-[#06B6D4] rounded-xl p-3.5 transition-colors">
                <div className="h-9 w-9 shrink-0 rounded-lg bg-[#06B6D4]/15 flex items-center justify-center"><Grid3x3 className="h-5 w-5 text-[#06B6D4]" /></div>
                <div>
                  <div className="font-semibold text-sm text-white">{t("studio.syncTitle")}</div>
                  <div className="text-xs text-[#8B98B0]">{t("studio.syncDesc")}</div>
                </div>
              </button>
              <button onClick={runAutoMix} data-testid="automix-button"
                className="group flex items-start gap-3 text-left bg-[#0B0D12] hover:bg-[#1c1430] border border-[#262C3A] hover:border-[#8B5CF6] rounded-xl p-3.5 transition-colors">
                <div className="h-9 w-9 shrink-0 rounded-lg bg-[#8B5CF6]/15 flex items-center justify-center"><SlidersHorizontal className="h-5 w-5 text-[#8B5CF6]" /></div>
                <div>
                  <div className="font-semibold text-sm text-white">{t("studio.automixTitle")}</div>
                  <div className="text-xs text-[#8B98B0]">{t("studio.automixDesc")}</div>
                </div>
              </button>
              <button onClick={runVoiceAnalysis} data-testid="voice-button"
                className="group flex items-start gap-3 text-left bg-[#0B0D12] hover:bg-[#30161f] border border-[#262C3A] hover:border-[#FF5500] rounded-xl p-3.5 transition-colors">
                <div className="h-9 w-9 shrink-0 rounded-lg bg-[#FF5500]/15 flex items-center justify-center"><Volume2 className="h-5 w-5 text-[#FF5500]" /></div>
                <div>
                  <div className="font-semibold text-sm text-white">{t("studio.voiceTitle")}</div>
                  <div className="text-xs text-[#8B98B0]">{t("studio.voiceDesc")}</div>
                </div>
              </button>
            </div>
            {voiceInfo && (
              <div className="mt-3 bg-[#0B0D12] border border-[#262C3A] rounded-xl p-3" data-testid="voice-sections">
                <div className="flex items-center justify-between mb-2 text-xs">
                  <span className="text-[#94A3B8]">{t("studio.voiceLabel")} <strong className="text-white">{voiceInfo.vocalName}</strong></span>
                  <span className="flex items-center gap-3">
                    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-[#FF5500]" /> {t("studio.refrain")}</span>
                    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-[#334155]" /> {t("studio.couplet")}</span>
                  </span>
                </div>
                <div className="flex h-7 w-full rounded-md overflow-hidden" title={t("studio.songStructure")}>
                  {voiceInfo.sections.map((s, i) => (
                    <div key={i} className={`h-full ${s.type === "refrain" ? "bg-[#FF5500]" : "bg-[#334155]"}`}
                      style={{ width: `${((s.end - s.start) / voiceInfo.totalSec) * 100}%` }}
                      title={`${s.type} · ${s.start}s–${s.end}s`} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* VOCAL PRO — harmonizer, lead chain, vocoder */}
          <div className="bg-gradient-to-br from-[#101a2e] to-[#1a1030] border border-[#2a3a5d] rounded-2xl p-4 mb-4" data-testid="vocal-pro">
            <div className="flex items-center gap-2 mb-3">
              <Mic2 className="h-4 w-4 text-[#FF5500]" />
              <span className="font-heading font-bold text-white">{t("studio.vocalProTitle")}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <button onClick={runHarmonize} data-testid="harmonize-button"
                className="group flex items-start gap-3 text-left bg-[#0B0D12] hover:bg-[#1c1430] border border-[#262C3A] hover:border-[#EC4899] rounded-xl p-3.5 transition-colors">
                <div className="h-9 w-9 shrink-0 rounded-lg bg-[#EC4899]/15 flex items-center justify-center"><Layers className="h-5 w-5 text-[#EC4899]" /></div>
                <div>
                  <div className="font-semibold text-sm text-white">{t("studio.harmonizeTitle")}</div>
                  <div className="text-xs text-[#8B98B0]">{t("studio.harmonizeDesc")}</div>
                </div>
              </button>
              <button onClick={runLeadVocalPro} data-testid="lead-vocal-button"
                className="group flex items-start gap-3 text-left bg-[#0B0D12] hover:bg-[#30161f] border border-[#262C3A] hover:border-[#FF5500] rounded-xl p-3.5 transition-colors">
                <div className="h-9 w-9 shrink-0 rounded-lg bg-[#FF5500]/15 flex items-center justify-center"><Volume2 className="h-5 w-5 text-[#FF5500]" /></div>
                <div>
                  <div className="font-semibold text-sm text-white">{t("studio.leadVocalTitle")}</div>
                  <div className="text-xs text-[#8B98B0]">{t("studio.leadVocalDesc")}</div>
                </div>
              </button>
            </div>
            <div className="bg-[#0B0D12] border border-[#262C3A] rounded-xl p-3" data-testid="vocoder-panel">
              <div className="flex items-center gap-2 mb-2 text-xs font-mono uppercase tracking-widest text-[#06B6D4]"><Radio className="h-3.5 w-3.5" /> {t("studio.vocoderTitle")}</div>
              <p className="text-xs text-[#8B98B0] mb-2">{t("studio.vocoderDesc")}</p>
              <div className="grid sm:grid-cols-3 gap-2">
                <TxSelect
                  value={vocMod} onValueChange={setVocMod} testid="vocoder-mod-select"
                  placeholder={t("studio.vocoderModPh")} triggerClassName="h-10 rounded-lg text-sm"
                  items={tracks.filter((x) => isVocalCategory(x.category)).map((x) => ({ value: x.id, label: x.name }))}
                />
                <TxSelect
                  value={vocCarrier} onValueChange={setVocCarrier} testid="vocoder-carrier-select"
                  placeholder={t("studio.vocoderCarrierPh")} triggerClassName="h-10 rounded-lg text-sm"
                  items={tracks.filter((x) => !isVocalCategory(x.category)).map((x) => ({ value: x.id, label: x.name }))}
                />
                <button onClick={runVocoder} data-testid="vocoder-apply-button"
                  className="inline-flex items-center justify-center gap-2 bg-[#06B6D4] hover:bg-[#0891b2] text-white rounded-lg px-3 py-2 text-sm font-semibold transition-colors">
                  <Radio className="h-4 w-4" /> {t("studio.vocoderApply")}
                </button>
              </div>
            </div>
          </div>

          {/* AI suggestion */}
          <div className="bg-gradient-to-r from-[#131722] to-[#1A1F2C] border border-[#262C3A] rounded-2xl p-4 mb-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#8B5CF6]" /><span className="font-semibold">{t("studio.assistantTitle")}</span></div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={runAiSuggest} data-testid="ai-suggest-button" className="inline-flex items-center gap-2 bg-[#1A1F2C] hover:bg-[#262C3A] rounded-xl px-4 py-2 text-sm font-semibold transition-colors">
                  <Wand2 className="h-4 w-4" /> {t("studio.analyzeSuggest")}
                </button>
                <button onClick={runAiPro} disabled={aiProLoading} data-testid="ai-pro-button" className="inline-flex items-center gap-2 bg-[#8B5CF6] hover:bg-[#7c3aed] disabled:opacity-50 rounded-xl px-4 py-2 text-sm font-semibold transition-colors">
                  {!isSub ? <Lock className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />} {aiProLoading ? t("ai.proLoading") : t("ai.proBtn")}
                </button>
              </div>
            </div>
            {aiText && <pre className="mt-3 text-sm text-[#cbd5e1] whitespace-pre-wrap font-sans leading-relaxed" data-testid="ai-suggestion-text">{aiText}</pre>}
            {aiPro && (
              <div className="mt-4 border-t border-[#262C3A] pt-4" data-testid="ai-pro-output">
                <div className="flex items-center gap-2 mb-2 text-xs font-mono uppercase tracking-widest text-[#8B5CF6]"><Sparkles className="h-3.5 w-3.5" /> {t("ai.proTitle")}</div>
                <div className="text-sm text-[#cbd5e1]"><Markdown text={aiPro} /></div>
              </div>
            )}
          </div>

          {/* STEMS / GROUPS */}
          <StemsPanel
            tracks={tracks} stemTrims={stemTrims} setStemGain={setStemGain}
            setStemMute={setStemMute} setStemSolo={setStemSolo} catColors={CAT_COLORS} t={t} />

          {/* MIXING CONSOLE (vertical channel strips + master) */}
          <MixerConsole
            tracks={tracks} anySolo={anySolo} updateTrack={updateTrack} toggleFx={toggleFx}
            removeTrack={removeTrack} setRole={setRole} playRef={playRef} playing={playing}
            processedLabel={processedLabel} catLabel={catLabel} voiceBadge={voiceBadge}
            t={t} catColors={CAT_COLORS} />
          <p className="text-xs text-[#64748B] mt-3">{t("studio.tip")}</p>
          <p className="text-xs text-[#64748B] mt-3">{t("studio.tip")}</p>
        </main>

        {/* RIGHT RACK */}
        <aside className="lg:w-80 shrink-0 border-t lg:border-t-0 lg:border-l border-[#262C3A] p-4 space-y-4 tx-dark-scroll overflow-auto">
          <div>
            <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Volume2 className="h-4 w-4 text-[#FF5500]" /> {t("studio.masterTitle")}</h3>
            <div className="space-y-3 bg-[#131722] border border-[#262C3A] rounded-2xl p-4">
              <Slider label={`${t("studio.volMaster")} ${Math.round(params.masterGain * 100)}%`} min={0} max={1.2} step={0.05} value={params.masterGain} onChange={(v) => setParams((p) => ({ ...p, masterGain: v }))} />
              <Slider label={`${t("studio.lufsTarget")} ${params.lufsTarget}`} min={-24} max={-6} step={1} value={params.lufsTarget} onChange={(v) => setParams((p) => ({ ...p, lufsTarget: v }))} />
              <Slider label={`${t("studio.compThreshold")} ${params.compThreshold}dB`} min={-40} max={0} step={1} value={params.compThreshold} onChange={(v) => setParams((p) => ({ ...p, compThreshold: v }))} />
              <Slider label={`${t("studio.compRatio")} ${params.compRatio}:1`} min={1} max={12} step={1} value={params.compRatio} onChange={(v) => setParams((p) => ({ ...p, compRatio: v }))} />
            </div>
          </div>
          <div>
            <h3 className="font-heading font-bold mb-3">{t("studio.fxRouting")}</h3>
            <div className="grid grid-cols-2 gap-2">
              <Toggle label="Reverb" on={params.reverb} onClick={() => setParams((p) => ({ ...p, reverb: !p.reverb }))} testid="reverb-toggle" big />
              <Toggle label="Delay" on={params.delay} onClick={() => setParams((p) => ({ ...p, delay: !p.delay }))} testid="delay-toggle" big />
              <Toggle label="Sidechain" on={params.sidechain} onClick={() => setParams((p) => ({ ...p, sidechain: !p.sidechain }))} testid="sidechain-toggle" big />
              <Toggle label="Loop" on={params.loop} onClick={() => setParams((p) => ({ ...p, loop: !p.loop }))} big />
              <Toggle label={t("studio.mono")} on={params.mono} onClick={() => setParams((p) => ({ ...p, mono: !p.mono }))} testid="mono-toggle" big />
            </div>
          </div>
          <div>
            <h3 className="font-heading font-bold mb-3">{t("studio.qualityExport")}</h3>
            <div className="mb-2">
              <TxSelect
                value={params.quality} onValueChange={(v) => setParams((p) => ({ ...p, quality: v }))}
                testid="quality-select" triggerClassName="w-full h-11 px-3"
                items={Object.keys(QUALITY).map((q) => ({ value: q, label: `${q} — ${QUALITY[q].label}` }))}
              />
            </div>
            <div className="mb-3">
              <TxSelect
                value={format} onValueChange={setFormat}
                testid="format-select" triggerClassName="w-full h-11 px-3"
                items={["WAV", "MP3", "OGG", "WebM"].map((f) => ({ value: f, label: `${t("studio.format")} ${f}${f !== "WAV" ? " " + t("studio.compressed") : ""}` }))}
              />
            </div>
            <div className="grid grid-cols-1 gap-2">
              <button onClick={exportMix} disabled={exporting} data-testid="export-button"
                className="inline-flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-50 rounded-xl py-3 font-semibold transition-colors">
                <Download className="h-4 w-4" /> {exporting ? t("studio.rendering") : `${t("studio.exportBtn")} ${format}`}
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={downloadReport} data-testid="report-button" className="inline-flex items-center justify-center gap-2 bg-[#1A1F2C] hover:bg-[#262C3A] rounded-xl py-2.5 text-sm transition-colors"><FileText className="h-4 w-4" /> {t("studio.report")}</button>
                <button onClick={() => saveMix()} data-testid="save-mix-button" className="inline-flex items-center justify-center gap-2 bg-[#1A1F2C] hover:bg-[#262C3A] rounded-xl py-2.5 text-sm transition-colors"><Save className="h-4 w-4" /> {t("studio.save")}</button>
              </div>
            </div>
          </div>
          <div>
            <button onClick={() => setShowHistory((s) => !s)} data-testid="history-toggle" className="w-full flex items-center justify-between font-heading font-bold">
              <span className="flex items-center gap-2"><HistoryIcon className="h-4 w-4 text-[#06B6D4]" /> {t("studio.history")} ({history.length})</span>
              <span className="text-[#64748B]">{showHistory ? "−" : "+"}</span>
            </button>
            {showHistory && (
              <div className="mt-3 space-y-2 max-h-64 overflow-auto tx-dark-scroll">
                {history.map((h, i) => (
                  <div key={i} className="bg-[#131722] border border-[#262C3A] rounded-xl p-3 text-sm">
                    <div className="font-semibold truncate">{h.name}</div>
                    <div className="font-mono text-xs text-[#64748B]">{h.quality} · {h.track_count} {t("studio.tracksWord")} · {h.bpm} BPM</div>
                  </div>
                ))}
                {!history.length && <p className="text-xs text-[#64748B]">{t("studio.noSavedMix")}</p>}
              </div>
            )}
          </div>
        </aside>
      </div>
      {(analyzing) && <div className="fixed bottom-4 left-4 bg-[#131722] border border-[#262C3A] rounded-xl px-4 py-2 text-sm z-40">{t("studio.analyzing")}</div>}
      <AiChatWidget mode="studio" buildContext={buildAiContext} canUse={isSub} onUpgrade={goPricing} userId={user?.id} />
    </div>
  );
}

function Slider({ label, min, max, step, value, onChange }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-[10px] text-[#94A3B8] uppercase">{label}</span>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full accent-[#FF5500]" />
    </div>
  );
}

function Toggle({ label, on, onClick, testid, big }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`rounded-lg font-semibold transition-colors ${big ? "py-2.5 text-sm" : "px-2 py-1 text-xs"} ${on ? "bg-[#FF5500] text-white" : "bg-[#1A1F2C] text-[#94A3B8] hover:bg-[#262C3A]"}`}>
      {label}
    </button>
  );
}
